import axios from "axios";
import { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../../context/AuthContext";
import { NavigationContext } from "../../context/NavigationContext";
import {
    Table,
    Card,
    Input,
    Row,
    Col,
    Select,
    Button,
    Modal,
    Typography,
    DatePicker,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as XLSX from "xlsx";
import dayjs from "dayjs";
import ViewEmployees from "../../components/ViewEmployees";

// Superuser tab statuses — mirrors Leave workflow exactly:
// Pending tab = "Pending" (records HR submitted for approval)
// Draft records are NOT shown to superuser — they are HR's working copies
const TAB_STATUSES = {
    Pending: ["Pending"],
    Approved: ["Approved"],
    Rejected: ["Rejected"],
};

const { Title } = Typography;

export default function SalaryDetails() {
    const { backUrl, authData } = useContext(AuthContext);

    /* ─── data ─── */
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);

    /* ─── tabs ─── */
    const [activeTab, setActiveTab] = useState("Pending");

    /* ─── filters ─── */
    const [filters, setFilters] = useState({
        name: "", emp_code: "", month: "", year: "",
        organisation_id: "", department_id: "", designation_id: "",
    });
    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    /* ─── reject modal ─── */
    const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [rejecting, setRejecting] = useState(false);

    /* ─── org-wise salary modal ─── */
    const [isOrgWiseModalVisible, setIsOrgWiseModalVisible] = useState(false);
    const [orgWiseMonth, setOrgWiseMonth] = useState(null);
    const [orgWiseYear, setOrgWiseYear] = useState(null);
    const [orgWiseLoading, setOrgWiseLoading] = useState(false);
    const [orgWiseData, setOrgWiseData] = useState([]);       // fetched results
    const [orgWiseGrandTotal, setOrgWiseGrandTotal] = useState(0);
    const [orgWiseFetched, setOrgWiseFetched] = useState(false); // controls form vs table view

    const [selectedOrgId, setSelectedOrgId] = useState(null);
    const [selectedOrgName, setSelectedOrgName] = useState(null);
    const [showEmployeesView, setShowEmployeesView] = useState(false);

    const handleOrgClick = (record) => {
        setSelectedOrgId(record.organisation_id);
        setSelectedOrgName(record.organisation_name); // adjust key to match your data
        setShowEmployeesView(true);
        closeOrgWiseModal(); // optional: close the modal when navigating away
    };

    const handleBackFromEmployees = () => {
        setShowEmployeesView(false);
        setSelectedOrgId(null);
        setSelectedOrgName(null);
    };

    /* ═══════════════════════════════════════════════════
       API
    ═══════════════════════════════════════════════════ */
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const headers = { Authorization: `Bearer ${authData?.access}` };

            const statuses = TAB_STATUSES[activeTab] || [activeTab];
            const p = new URLSearchParams();
            p.append("salary_status", statuses.join(","));
            if (filters.month) p.append("month", filters.month);
            if (filters.year) p.append("year", filters.year);

            const res = await axios.get(
                `${backUrl}/api/payroll/drafts/?${p.toString()}`,
                { headers }
            );

            const results = res.data.results || [];
            setData(results);
            setFilteredData(results);
            setSelectedRowKeys([]);
        } catch (err) {
            console.error("Error fetching salary data:", err);
            toast.error("Failed to fetch salary data");
        } finally {
            setLoading(false);
        }
    }, [authData, activeTab, filters.month, filters.year, backUrl]);

    const fetchOrganisations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`,
                { headers: { Authorization: `Bearer ${authData?.access}` } });
            setOrganisations(res.data.results || []);
        } catch (err) { console.error(err); }
    };

    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${orgId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } });
            setDepartments(res.data.results || []);
        } catch (err) { console.error(err); }
    };

    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/?department=${deptId}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } });
            setDesignations(res.data.results || []);
        } catch (err) { console.error(err); }
    };

    const { navigationParams, clearNavigationParams } = useContext(NavigationContext);

    useEffect(() => {
        if (navigationParams?.status) {
            console.log("🎯 SalaryDetails: Syncing status from navigation:", navigationParams.status);
            // SalaryDetails uses PascalCase: "Pending", "Approved", "Rejected"
            const status = navigationParams.status.charAt(0).toUpperCase() + navigationParams.status.slice(1).toLowerCase();
            if (["Pending", "Approved", "Rejected"].includes(status)) {
                setActiveTab(status);
            }
            clearNavigationParams();
        }
    }, [navigationParams, clearNavigationParams]);

    /* ═══════════════════════════════════════════════════
       Effects
    ═══════════════════════════════════════════════════ */
    // Re-fetch when tab / month / year changes (server-side)
    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => { fetchOrganisations(); }, []);

    // Client-side filtering for name / emp_code / org / dept / desig
    useEffect(() => {
        let f = [...data];
        if (filters.emp_code)
            f = f.filter(i => i.employee?.emp_code?.toLowerCase().includes(filters.emp_code.toLowerCase()));
        if (filters.name)
            f = f.filter(i => `${i.employee?.first_name || ""} ${i.employee?.last_name || ""}`.toLowerCase().includes(filters.name.toLowerCase()));
        if (filters.organisation_id)
            f = f.filter(i => String(i.employee?.organisation_id) === String(filters.organisation_id));
        if (filters.department_id)
            f = f.filter(i => String(i.employee?.department_id) === String(filters.department_id));
        if (filters.designation_id)
            f = f.filter(i => String(i.employee?.designation_id) === String(filters.designation_id));
        setFilteredData(f);
    }, [filters.emp_code, filters.name, filters.organisation_id, filters.department_id, filters.designation_id, data]);

    /* ═══════════════════════════════════════════════════
       Actions
    ═══════════════════════════════════════════════════ */
    const handleBulkApprove = async () => {
        if (!selectedRowKeys.length) { toast.warning("Please select at least one record!"); return; }
        try {
            const res = await axios.post(
                `${backUrl}/api/payroll/bulk-approve-salary/`,
                { ids: selectedRowKeys },
                { headers: { Authorization: `Bearer ${authData?.access}`, "Content-Type": "application/json" } }
            );
            toast.success(res.data.message || `${selectedRowKeys.length} salaries approved!`);
            setSelectedRowKeys([]);
            fetchData();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.detail || err.response?.data?.error || "Failed to approve salaries");
        }
    };

    const handleBulkReject = async () => {
        if (!rejectReason.trim()) { toast.warning("Please enter a rejection reason!"); return; }
        setRejecting(true);
        try {
            const res = await axios.post(
                `${backUrl}/api/payroll/bulk-reject-salary/`,
                { ids: selectedRowKeys, reason: rejectReason },
                { headers: { Authorization: `Bearer ${authData?.access}`, "Content-Type": "application/json" } }
            );
            toast.success(res.data.message || `${selectedRowKeys.length} salaries rejected!`);
            setSelectedRowKeys([]);
            setIsRejectModalVisible(false);
            setRejectReason("");
            fetchData();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.detail || err.response?.data?.error || "Failed to reject salaries");
        } finally {
            setRejecting(false);
        }
    };

    /* ------------------- Download Excel ------------------- */
    // Field set now mirrors ActualSalaryStructure exactly:
    //   • Other Allowances     → Special Allowance              (RENAMED)
    //   • Flexible Benefit Allowance                             (NEW)
    //   • Gross Salary                                           (NEW)
    //   • TDS                                                    (NEW)
    //   • Total Deductions                                       (NEW column, was missing)
    const handleExportExcel = () => {
        if (!filteredData.length) { toast.warning("No data to export!"); return; }
        const rows = filteredData.map(item => ({
            "Emp Code": item.employee?.emp_code || "",
            "Name": `${item.employee?.first_name || ""} ${item.employee?.last_name || ""}`.trim(),
            "Organisation": item.employee?.organisation_name || "",
            "Department": item.employee?.department_name || "",
            "Designation": item.employee?.designation_name || "",
            "Month": item.month ? dayjs().month(item.month - 1).format("MMMM") : "",
            "Year": item.year || "",
            "Month Days": item.month_days || "",
            "Days Payable": item.days_payable || "",
            "Basic": item.basic_earned || "",
            "HRA": item.hra_earned || "",
            "Conveyance": item.conveyance_earned || "",
            // RENAMED: Other Allowances → Special Allowance
            "Special Allowance": item.special_allowance_earned || "",
            // NEW
            "Flexible Benefit Allowance": item.flexible_benefit_allowance || "",
            "Gross Earned": item.gross_earned || "",
            // NEW
            "Gross Salary": item.gross_salary || "",
            "OT Days": item.no_of_ot_days || "",
            "OT Pay": item.ot_pay || "",
            "PT": item.pt_pm || "",
            "Employer PF (12%)": item.pf_employer || "",
            "Employer ESI (3.25%)": item.esi_employer || "",
            "Employee PF": item.pf_employee || "",
            "Employee ESI": item.esi_employee || "",
            // NEW
            "TDS": item.tds || "",
            "Late Days": item.late_days || "",
            "Late Deduction": item.late_deduction || "",
            "LOP Days": item.lop_days || "",
            "LOP Deduction": item.lop_deduction || "",
            "Salary Advance": item.salary_advance || "",
            "Arrears": item.arrears || "",
            "Total CTC": item.ctc_monthly || "",
            // NEW
            "Total Deductions": item.total_deductions || "",
            "Net Pay": item.net_pay || "",
            "Net Pay in Words": item.net_pay_in_words || "",
            "Salary Type": item.salary_type || "",
            "Salary Status": item.salary_status || "",
            "CL Total": item.leave_balance?.CL?.total ?? "",
            "CL Used": item.leave_balance?.CL?.used ?? "",
            "CL Balance": item.leave_balance?.CL?.balance ?? "",
            "SL Total": item.leave_balance?.SL?.total ?? "",
            "SL Used": item.leave_balance?.SL?.used ?? "",
            "SL Balance": item.leave_balance?.SL?.balance ?? "",
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Monthly Salary");
        XLSX.writeFile(wb, "Monthly_Salary_Statement.xlsx");
        toast.success("Excel downloaded!");
    };

    /* ------------------- Organisation Wise Salary ------------------- */
    const openOrgWiseModal = () => {
        setOrgWiseMonth(null);
        setOrgWiseYear(null);
        setOrgWiseData([]);
        setOrgWiseGrandTotal(0);
        setOrgWiseFetched(false);
        setIsOrgWiseModalVisible(true);
    };

    const closeOrgWiseModal = () => {
        setIsOrgWiseModalVisible(false);
        setOrgWiseMonth(null);
        setOrgWiseYear(null);
        setOrgWiseData([]);
        setOrgWiseGrandTotal(0);
        setOrgWiseFetched(false);
    };

    const handleOrgWiseSalary = async () => {
        if (!orgWiseMonth) { toast.warning("Please select month!"); return; }
        if (!orgWiseYear) { toast.warning("Please select year!"); return; }

        setOrgWiseLoading(true);
        try {
            const res = await axios.get(
                `${backUrl}/api/payroll/salary/org/${orgWiseMonth}/${orgWiseYear}/`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );

            const results = res.data.results || [];
            setOrgWiseData(results);
            setOrgWiseGrandTotal(res.data.grand_total_net_pay ?? 0);
            setOrgWiseFetched(true);

            if (!results.length) {
                toast.info("No organisations found!");
            } else {
                toast.success("Organisation wise salary fetched!");
            }
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.detail || err.response?.data?.error || "Failed to fetch organisation wise salary");
        } finally {
            setOrgWiseLoading(false);
        }
    };

    const handleOrgWiseBack = () => {
        setOrgWiseFetched(false);
        setOrgWiseData([]);
        setOrgWiseGrandTotal(0);
    };

    const handleExportOrgWiseExcel = () => {
        if (!orgWiseData.length) { toast.warning("No data to export!"); return; }
        const rows = orgWiseData.map(item => ({
            "Organisation": item.organisation_name || "",
            "Code": item.organisation_code || "",
            "Total Net Pay": item.total_net_pay || 0,
        }));
        rows.push({ "Organisation": "Grand Total", "Code": "", "Total Net Pay": orgWiseGrandTotal });
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Org Wise Salary");
        XLSX.writeFile(wb, `Organisation_Wise_Salary_${orgWiseMonth}_${orgWiseYear}.xlsx`);
        toast.success("Excel downloaded!");
    };

    /* org-wise table columns */
    const orgWiseColumns = [
        {
            title: "Organisation",
            dataIndex: "organisation_name",
            key: "organisation_name",
            render: (text, record) => (
                <a
                    onClick={() => handleOrgClick(record)}
                    style={{ color: "#0e497b", fontWeight: 500, cursor: "pointer" }}
                >
                    {text}
                </a>
            ),
        },
        {
            title: "Code", dataIndex: "organisation_code", key: "organisation_code", width: 100,
        },
        {
            title: "Total Net Pay", dataIndex: "total_net_pay", key: "total_net_pay", width: 160,
            align: "right",
            render: val => Number(val || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
        },
    ];

    /* ═══════════════════════════════════════════════════
       Table columns — field set mirrors ActualSalaryStructure
    ═══════════════════════════════════════════════════ */
    const columns = [
        { title: "Emp Code", dataIndex: ["employee", "emp_code"], key: "emp_code", width: 120 },
        {
            title: "Name", key: "name", width: 180,
            render: (_, r) => `${r.employee?.first_name || ""} ${r.employee?.last_name || ""}`.trim()
        },
        { title: "Organisation", dataIndex: ["employee", "organisation_name"], key: "org", width: 180 },
        { title: "Department", dataIndex: ["employee", "department_name"], key: "dept", width: 180 },
        { title: "Designation", dataIndex: ["employee", "designation_name"], key: "desig", width: 180 },
        {
            title: "Month", dataIndex: "month", key: "month", width: 120,
            render: m => { const n = parseInt(m, 10); return n >= 1 && n <= 12 ? dayjs().month(n - 1).format("MMMM") : "-"; }
        },
        { title: "Year", dataIndex: "year", key: "year", width: 90 },
        { title: "Month Days", dataIndex: "month_days", key: "month_days", width: 110 },
        { title: "Days Payable", dataIndex: "days_payable", key: "days_payable", width: 120 },
        { title: "Basic", dataIndex: "basic_earned", key: "basic", width: 110 },
        { title: "HRA", dataIndex: "hra_earned", key: "hra", width: 110 },
        { title: "Conveyance", dataIndex: "conveyance_earned", key: "conv", width: 120 },

        // RENAMED: Other Allowances → Special Allowance
        { title: "Special Allowance", dataIndex: "special_allowance_earned", key: "special_allowance", width: 150 },

        // NEW: Flexible Benefit Allowance
        { title: "Flexible Benefit Allowance", dataIndex: "flexible_benefit_allowance", key: "fba", width: 180 },

        { title: "Gross Earned", dataIndex: "gross_earned", key: "gross_earned", width: 120 },

        // NEW: Gross Salary (as per salary structure)
        { title: "Gross Salary", dataIndex: "gross_salary", key: "gross_salary", width: 130 },

        { title: "OT Days", dataIndex: "no_of_ot_days", key: "ot_days", width: 100 },
        { title: "OT Pay", dataIndex: "ot_pay", key: "ot_pay", width: 100 },
        { title: "PT", dataIndex: "pt_pm", key: "pt", width: 80 },
        { title: "Employer PF (12%)", dataIndex: "pf_employer", key: "pf_emp", width: 140 },
        { title: "Employer ESI (3.25%)", dataIndex: "esi_employer", key: "esi_emp", width: 150 },
        { title: "Employee PF", dataIndex: "pf_employee", key: "pf_ee", width: 120 },
        { title: "Employee ESI", dataIndex: "esi_employee", key: "esi_ee", width: 120 },

        // NEW: TDS
        { title: "TDS", dataIndex: "tds", key: "tds", width: 100 },

        { title: "Late Days", dataIndex: "late_days", key: "late_days", width: 100 },
        { title: "Late Deduct.", dataIndex: "late_deduction", key: "late_ded", width: 110 },
        { title: "LOP Days", dataIndex: "lop_days", key: "lop_days", width: 100 },
        { title: "LOP Deduct.", dataIndex: "lop_deduction", key: "lop_ded", width: 110 },
        { title: "Salary Adv.", dataIndex: "salary_advance", key: "sal_adv", width: 110 },
        { title: "Arrears", dataIndex: "arrears", key: "arrears", width: 100 },
        { title: "Total CTC", dataIndex: "ctc_monthly", key: "ctc_monthly", width: 110 },

        // NEW: Total Deductions column (was missing from table)
        { title: "Total Deductions", dataIndex: "total_deductions", key: "total_ded", width: 140 },

        { title: "Net Pay", dataIndex: "net_pay", key: "net_pay", width: 110 },
        { title: "Net Pay (Words)", dataIndex: "net_pay_in_words", key: "net_words", width: 220 },
        { title: "Salary Type", dataIndex: "salary_type", key: "salary_type", width: 110 },

        { title: "CL Total", key: "cl_total", render: (_, r) => r.leave_balance?.CL?.total ?? "-", width: 90 },
        { title: "CL Used", key: "cl_used", render: (_, r) => r.leave_balance?.CL?.used ?? "-", width: 90 },
        { title: "CL Balance", key: "cl_balance", render: (_, r) => r.leave_balance?.CL?.balance ?? "-", width: 90 },
        { title: "SL Total", key: "sl_total", render: (_, r) => r.leave_balance?.SL?.total ?? "-", width: 90 },
        { title: "SL Used", key: "sl_used", render: (_, r) => r.leave_balance?.SL?.used ?? "-", width: 90 },
        { title: "SL Balance", key: "sl_balance", render: (_, r) => r.leave_balance?.SL?.balance ?? "-", width: 90 },
        {
            title: "Status", dataIndex: "salary_status", key: "status", width: 160, fixed: "right",
            render: text => {
                const map = { "Pending": "#faad14", "Approved": "#52c41a", "Rejected": "#ff4d4f" };
                return <span style={{ fontWeight: 600, color: map[text] || "#000" }}>{text || "-"}</span>;
            },
        },
    ];

    /* ═══════════════════════════════════════════════════
       Render
    ═══════════════════════════════════════════════════ */
    const TABS = [
        { key: "Pending", label: "Pending", color: "#faad14" },
        { key: "Approved", label: "Approved", color: "#52c41a" },
        { key: "Rejected", label: "Rejected", color: "#ff4d4f" },
    ];

    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} />

            <center>
                <Title level={4} style={{ marginTop: 20, color: "#0e497b", textTransform: "uppercase" }}>
                    Monthly Salary Statement
                </Title>
            </center>

            {/* ── Status Tabs ── */}
            <div style={{ textAlign: "center", marginBottom: 20 }}>
                {TABS.map(tab => (
                    <Button
                        key={tab.key}
                        type={activeTab === tab.key ? "primary" : "default"}
                        onClick={() => { setActiveTab(tab.key); setSelectedRowKeys([]); }}
                        style={{
                            marginRight: 10, marginBottom: 8,
                            backgroundColor: activeTab === tab.key ? tab.color : undefined,
                            borderColor: activeTab === tab.key ? tab.color : undefined,
                            color: activeTab === tab.key ? "#fff" : undefined,
                            fontWeight: "500",
                        }}
                    >
                        {tab.label}
                    </Button>
                ))}
            </div>

            {/* ── Filters ── */}
            <Card style={{ margin: "0 20px 20px 20px" }}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={6} lg={3}>
                        <Input placeholder="Emp Code" value={filters.emp_code} allowClear
                            onChange={e => setFilters({ ...filters, emp_code: e.target.value })} />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={3}>
                        <Input placeholder="Name" value={filters.name} allowClear
                            onChange={e => setFilters({ ...filters, name: e.target.value })} />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select placeholder="Organisation" style={{ width: "100%" }}
                            value={filters.organisation_id || undefined} allowClear
                            onChange={v => { setFilters({ ...filters, organisation_id: v || "", department_id: "", designation_id: "" }); setDepartments([]); setDesignations([]); if (v) fetchDepartments(v); }}>
                            {organisations.map(o => <Select.Option key={o.id} value={o.id}>{o.name}</Select.Option>)}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select placeholder="Department" style={{ width: "100%" }}
                            value={filters.department_id || undefined} allowClear disabled={!filters.organisation_id}
                            onChange={v => { setFilters({ ...filters, department_id: v || "", designation_id: "" }); setDesignations([]); if (v) fetchDesignations(v); }}>
                            {departments.map(d => <Select.Option key={d.id} value={d.id}>{d.name}</Select.Option>)}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <Select placeholder="Designation" style={{ width: "100%" }}
                            value={filters.designation_id || undefined} allowClear disabled={!filters.department_id}
                            onChange={v => setFilters({ ...filters, designation_id: v || "" })}>
                            {designations.map(d => <Select.Option key={d.id} value={d.id}>{d.name}</Select.Option>)}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={3}>
                        <Select placeholder="Month" style={{ width: "100%" }}
                            value={filters.month || undefined} allowClear
                            onChange={v => setFilters({ ...filters, month: v })}>
                            {Array.from({ length: 12 }, (_, i) => (
                                <Select.Option key={i + 1} value={i + 1}>{dayjs().month(i).format("MMMM")}</Select.Option>
                            ))}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={2}>
                        <Input placeholder="Year" type="number" value={filters.year} allowClear
                            onChange={e => setFilters({ ...filters, year: e.target.value })} />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={2}>
                        <Button style={{ width: "100%" }}
                            onClick={() => { setFilters({ name: "", emp_code: "", month: "", year: "", organisation_id: "", department_id: "", designation_id: "" }); setDepartments([]); setDesignations([]); }}>
                            Reset
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* ── Action Bar + Table ── */}
            <Card style={{ margin: "0 20px 20px 20px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
                    {/* Left: counters */}
                    <div style={{ display: "flex", gap: 12 }}>
                        <Card size="small" style={{ borderRadius: 8, background: "#f0f5ff", border: "1px solid #adc6ff" }}>
                            <span style={{ color: "#2f54eb", fontWeight: "bold" }}>Total: </span>
                            <span style={{ fontSize: 16, fontWeight: "bold" }}>{filteredData.length}</span>
                        </Card>
                        {selectedRowKeys.length > 0 && (
                            <Card size="small" style={{ borderRadius: 8, background: "#f6ffed", border: "1px solid #b7eb8f" }}>
                                <span style={{ color: "#52c41a", fontWeight: "bold" }}>Selected: </span>
                                <span style={{ fontSize: 16, fontWeight: "bold" }}>{selectedRowKeys.length}</span>
                            </Card>
                        )}
                    </div>

                    {/* Right: action buttons */}
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        {/* Approve / Reject only on Pending tab (covers both Pending + Waiting for Approval records) */}
                        {activeTab === "Pending" && selectedRowKeys.length > 0 && (
                            <>
                                <Button
                                    type="primary"
                                    style={{ backgroundColor: "#52c41a", borderColor: "#52c41a", fontWeight: "500" }}
                                    onClick={handleBulkApprove}
                                >
                                    Approve ({selectedRowKeys.length})
                                </Button>
                                <Button
                                    danger type="primary"
                                    style={{ fontWeight: "500" }}
                                    onClick={() => { setRejectReason(""); setIsRejectModalVisible(true); }}
                                >
                                    Reject ({selectedRowKeys.length})
                                </Button>
                            </>
                        )}
                        <Button
                            style={{ backgroundColor: "#0e497b", borderColor: "#0e497b", color: "white", fontWeight: "500" }}
                            onClick={openOrgWiseModal}
                        >
                            Salary (Organisation Wise)
                        </Button>
                        <Button
                            icon={<DownloadOutlined />}
                            style={{ backgroundColor: "#0e497b", borderColor: "#0e497b", color: "white", fontWeight: "500" }}
                            onClick={handleExportExcel}
                        >
                            Export Excel
                        </Button>
                    </div>
                </div>

                <Table
                    rowSelection={{
                        selectedRowKeys,
                        onChange: keys => setSelectedRowKeys(keys),
                        // All rows on Pending tab are "Waiting for Approval" — all selectable
                        getCheckboxProps: () => ({}),
                    }}
                    columns={columns}
                    dataSource={filteredData}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    bordered
                    loading={loading}
                    scroll={{ x: 6500 }}
                    components={{
                        header: {
                            cell: props => (
                                <th {...props} style={{ backgroundColor: "#0e497b", color: "white", textAlign: "center" }}>
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            </Card>

            {/* ── Reject Modal ── */}
            <Modal
                open={isRejectModalVisible}
                onCancel={() => { setIsRejectModalVisible(false); setRejectReason(""); }}
                title={`Reject ${selectedRowKeys.length} Salary Record(s)`}
                footer={[
                    <Button key="cancel" onClick={() => { setIsRejectModalVisible(false); setRejectReason(""); }}>
                        Cancel
                    </Button>,
                    <Button key="reject" danger type="primary" loading={rejecting}
                        disabled={!rejectReason.trim()} onClick={handleBulkReject}>
                        Confirm Reject
                    </Button>,
                ]}
                width={480}
            >
                <p style={{ marginBottom: 8, fontWeight: 500 }}>
                    Rejection Reason <span style={{ color: "red" }}>*</span>
                </p>
                <Input.TextArea
                    rows={4}
                    placeholder="Enter reason for rejection..."
                    value={rejectReason}
                    onChange={e => setRejectReason(e.target.value)}
                />
            </Modal>
            {/* ── Organisation Wise Salary Modal ── */}
            {showEmployeesView ? (
                <ViewEmployees
                    organisationId={selectedOrgId}
                    organisationName={selectedOrgName}
                    onBack={handleBackFromEmployees}
                />
            ) : (
                <>
                    {/* your existing page content, including the Modal below */}
                    <Modal
                        open={isOrgWiseModalVisible}
                        onCancel={closeOrgWiseModal}
                        title={
                            orgWiseFetched
                                ? `Salary (Organisation Wise) — ${dayjs().month(orgWiseMonth - 1).format("MMMM")} ${orgWiseYear}`
                                : "Salary (Organisation Wise)"
                        }
                        footer={
                            orgWiseFetched
                                ? [
                                    <button key="back" onClick={handleOrgWiseBack} style={{ cursor: "pointer" }}>
                                        ← Back
                                    </button>,
                                    <Button
                                        key="export"
                                        icon={<DownloadOutlined />}
                                        style={{ backgroundColor: "#0e497b", borderColor: "#0e497b", color: "white" }}
                                        onClick={handleExportOrgWiseExcel}
                                        disabled={!orgWiseData.length}
                                    >
                                        Export Excel
                                    </Button>,
                                    <Button key="close" onClick={closeOrgWiseModal}>
                                        Close
                                    </Button>,
                                ]
                                : [
                                    <Button key="cancel" onClick={closeOrgWiseModal}>
                                        Cancel
                                    </Button>,
                                    <Button
                                        key="submit"
                                        type="primary"
                                        loading={orgWiseLoading}
                                        disabled={!orgWiseMonth || !orgWiseYear}
                                        style={{ backgroundColor: "#0e497b", borderColor: "#0e497b" }}
                                        onClick={handleOrgWiseSalary}
                                    >
                                        Get Salary
                                    </Button>,
                                ]
                        }
                        width={orgWiseFetched ? 620 : 420}
                    >
                        {!orgWiseFetched ? (
                            <Row gutter={[16, 16]}>
                                <Col span={12}>
                                    <p style={{ marginBottom: 8, fontWeight: 500 }}>
                                        Month <span style={{ color: "red" }}>*</span>
                                    </p>
                                    <Select
                                        placeholder="Select Month"
                                        style={{ width: "100%" }}
                                        value={orgWiseMonth || undefined}
                                        onChange={v => setOrgWiseMonth(v)}
                                    >
                                        {Array.from({ length: 12 }, (_, i) => (
                                            <Select.Option key={i + 1} value={i + 1}>{dayjs().month(i).format("MMMM")}</Select.Option>
                                        ))}
                                    </Select>
                                </Col>
                                <Col span={12}>
                                    <p style={{ marginBottom: 8, fontWeight: 500 }}>
                                        Year <span style={{ color: "red" }}>*</span>
                                    </p>
                                    <Input
                                        placeholder="Enter Year"
                                        type="number"
                                        value={orgWiseYear || ""}
                                        onChange={e => setOrgWiseYear(e.target.value)}
                                    />
                                </Col>
                            </Row>
                        ) : (
                            <Table
                                columns={orgWiseColumns}
                                dataSource={orgWiseData}
                                rowKey="organisation_id"
                                pagination={false}
                                bordered
                                loading={orgWiseLoading}
                                size="small"
                                summary={() => (
                                    <Table.Summary fixed>
                                        <Table.Summary.Row>
                                            <Table.Summary.Cell index={0} colSpan={2}>
                                                <strong>Grand Total</strong>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={1} align="right">
                                                <strong>
                                                    {Number(orgWiseGrandTotal || 0).toLocaleString("en-IN", {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })}
                                                </strong>
                                            </Table.Summary.Cell>
                                        </Table.Summary.Row>
                                    </Table.Summary>
                                )}
                                components={{
                                    header: {
                                        cell: props => (
                                            <th {...props} style={{ backgroundColor: "#0e497b", color: "white" }}>
                                                {props.children}
                                            </th>
                                        ),
                                    },
                                }}
                            />
                        )}
                    </Modal>
                </>
            )}
        </>
    );
}